/**
 * Organize the Cases portfolio by operational state.
 * Views only — never delete, merge, or hide history.
 */

export const CASE_OPERATIONAL_BUCKETS = [
  'active_work',
  'monitoring',
  'unowned',
  'closed',
] as const;

export type CaseOperationalBucket = (typeof CASE_OPERATIONAL_BUCKETS)[number];

export const CASE_OPERATIONAL_LABELS: Record<CaseOperationalBucket, string> = {
  active_work: 'Active work',
  monitoring: 'Monitoring',
  unowned: 'Unowned',
  closed: 'Closed / archived',
};

export const CASE_OPERATIONAL_HELPERS: Record<CaseOperationalBucket, string> = {
  active_work: 'Cases that need action now.',
  monitoring: 'Cases with no current action.',
  unowned: 'Cases that still need a Case owner.',
  closed: 'Historical Cases. History stays intact.',
};

type CaseGroupingInput = {
  nextAction?: string | null;
  owner?: string | null;
  ownerUnresolved?: boolean | null;
  controlStage?: string | null;
  newActivity?: string | null;
};

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isClosedStage(stage: string): boolean {
  return stage === 'closed' || stage === 'archived' || stage.includes('closed');
}

function isUnowned(input: CaseGroupingInput): boolean {
  if (input.ownerUnresolved) return true;
  const owner = normalize(input.owner);
  return !owner || owner === 'unassigned' || owner === 'unowned' || owner === 'none';
}

function hasNewActivity(value: unknown): boolean {
  const label = normalize(value);
  return Boolean(label) && label !== 'no' && label !== '—' && label !== '-';
}

export function caseOperationalBucket(input: CaseGroupingInput): CaseOperationalBucket {
  const stage = normalize(input.controlStage);
  if (isClosedStage(stage)) return 'closed';
  if (isUnowned(input) && !isClosedStage(stage)) return 'unowned';
  const next = normalize(input.nextAction);
  const monitoring = stage === 'monitoring' || next === 'monitoring' || next === 'continue monitoring';
  if (monitoring && !hasNewActivity(input.newActivity) && next !== 'review new activity') {
    return 'monitoring';
  }
  return 'active_work';
}

export function groupCasesByOperationalBucket<T extends CaseGroupingInput>(
  rows: T[],
): Array<{ bucket: CaseOperationalBucket; label: string; helper: string; rows: T[] }> {
  const grouped = new Map<CaseOperationalBucket, T[]>(
    CASE_OPERATIONAL_BUCKETS.map((bucket) => [bucket, []]),
  );
  rows.forEach((row) => {
    grouped.get(caseOperationalBucket(row))?.push(row);
  });
  return CASE_OPERATIONAL_BUCKETS
    .map((bucket) => ({
      bucket,
      label: CASE_OPERATIONAL_LABELS[bucket],
      helper: CASE_OPERATIONAL_HELPERS[bucket],
      rows: grouped.get(bucket) || [],
    }))
    .filter((section) => section.rows.length > 0);
}
